// 2026-08-23 theme — system/dark/light, localStorage, prefers-color-scheme, no FOUC
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type ThemeMode = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

interface ThemeContextType {
  theme: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
}

const STORAGE_KEY = "vortixy-theme";

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // 2026-09-07 Sentry JAVASCRIPT-NEXTJS-E: el inicializador leía localStorage
  // en el primer render del cliente (p. ej. "light" guardado) mientras el SSR
  // siempre renderizaba "dark" → React error #418 hydration mismatch en `/`.
  // Estado inicial estable para SSR + sincronización en montaje (efecto).
  const [theme, setThemeState] = useState<ThemeMode>("dark");

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("dark");

  // Hidratar preferencia guardada solo en cliente, tras el primer render.
  // Patrón documentado anti-hydration-mismatch (estado SSR estable + sync en
  // montaje, una sola vez, sin dependencias → sin cascadas).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (saved === "dark" || saved === "light" || saved === "system") {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- mount-once hydration sync from localStorage, no loop (deps [])
        setThemeState(saved);
      }
    } catch {
      // storage bloqueado — se queda el default SSR
    }
  }, []);

  // Keep DOM attribute & resolved theme in sync
  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (currentMode: ThemeMode) => {
      let target: ResolvedTheme = "dark";
      if (currentMode === "system") {
        target = getSystemTheme();
      } else {
        target = currentMode;
      }
      setResolvedTheme(target);

      const updateDom = () => {
        root.setAttribute("data-theme", target);
        root.style.colorScheme = target;
      };

      // 2026 View Transition best practice (ViewTransition spec + React #34098 + TanStack #7907):
      // Chrome 146+ throws InvalidStateError "Transition was aborted because of invalid state"
      // when startViewTransition is called while document.visibilityState !== 'visible' or tab throttled.
      // This is benign — spec says skip when hidden. Guard visibility, reduced-motion, and swallow lifecycle rejections.
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (
        prefersReducedMotion ||
        typeof document === "undefined" ||
        document.visibilityState !== "visible"
      ) {
        updateDom();
        return;
      }

      if (typeof document !== "undefined" && "startViewTransition" in document) {
        try {
          const transition = (
            document as {
              startViewTransition?: (cb: () => void) => {
                ready: Promise<unknown>;
                finished: Promise<unknown>;
                updateCallbackDone: Promise<unknown>;
              };
            }
          ).startViewTransition?.(updateDom) as
            | {
                ready: Promise<unknown>;
                finished: Promise<unknown>;
                updateCallbackDone: Promise<unknown>;
              }
            | undefined;
          // 2026: ViewTransition promises reject benignly mid-transition (hidden, Abort, Timeout).
          // TanStack fix retains the object and swallows ready/finished. Chromium docs say these are expected.
          const swallowViewTransitionRejection = (promise?: Promise<unknown>) => {
            if (!promise?.catch) return;
            promise.catch((err: unknown) => {
              if (err instanceof DOMException) {
                if (["AbortError", "InvalidStateError", "TimeoutError"].includes(err.name)) return;
              }
              const msg = String(
                (err as { message?: unknown })?.message ?? err ?? ""
              ).toLowerCase();
              if (
                msg.includes("transition") &&
                (msg.includes("aborted") ||
                  msg.includes("skipped") ||
                  msg.includes("invalid state"))
              )
                return;
              // Unexpected: surface once for debugging (reportError preferred, fallback console.error)
              if (
                typeof (globalThis as unknown as { reportError?: (e: unknown) => void })
                  .reportError === "function"
              ) {
                try {
                  (globalThis as unknown as { reportError: (e: unknown) => void }).reportError(err);
                } catch {}
              } else if (process.env.NODE_ENV !== "production") {
                console.warn("[theme] view transition unexpected error:", err);
              }
            });
          };
          swallowViewTransitionRejection(transition?.ready);
          swallowViewTransitionRejection(transition?.finished);
          swallowViewTransitionRejection(transition?.updateCallbackDone);
          return;
        } catch (err) {
          // Synchronous throw when hidden/throttled (InvalidStateError) — benign, just apply without animation.
          if (
            err instanceof DOMException &&
            ["InvalidStateError", "AbortError"].includes(err.name)
          ) {
            updateDom();
            return;
          }
          const msg = String((err as { message?: unknown })?.message ?? "").toLowerCase();
          if (msg.includes("transition") && msg.includes("invalid state")) {
            updateDom();
            return;
          }
          updateDom();
          return;
        }
      }
      root.classList.add("theme-transitioning");
      updateDom();
      window.setTimeout(() => root.classList.remove("theme-transitioning"), 400);
    };

    applyTheme(theme);

    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => applyTheme("system");
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {
      // Storage access error handling
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: "dark",
      resolvedTheme: "dark",
      setTheme: () => {},
    };
  }
  return context;
}
